import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const posts = {
    'emerald-guide': {
      title: 'Emerald',
      subtitle: 'The Stone of Royalty & Rebirth',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/57qw7w56_F683E346-12CC-4A47-A850-08225E60D944.png',
      content: [
        {
          heading: 'Origins We Offer',
          items: [
            '<strong>Zambia</strong> — Vivid deep greens, exceptional clarity for emeralds, strong saturation',
            '<strong>Colombia</strong> — World&apos;s most famous origin, glowing &quot;green fire,&quot; extremely rare',
            '<strong>Panjshir Valley, Afghanistan</strong> — Legendary source of mesmerizing emeralds with sublime crystalline structure, coveted for their luminous verdant hues and extraordinary transparency. These rare treasures from the Hindu Kush mountains possess an ethereal beauty that rivals the finest Colombian stones.',
            '<strong>Brazil</strong> — Lighter greens, excellent brilliance, great value'
          ]
        },
        {
          heading: 'Why Emeralds Are Exceptional',
          items: [
            'Known as the &quot;King of Green.&quot;',
            'Associated with renewal, abundance, and intuition.',
            'Historically worn by Egyptian pharaohs, Mughal emperors, and European royalty.',
            'Highly collectible due to their natural inclusions (called jardin), which make every emerald unique.'
          ]
        },
        {
          heading: 'Why Collectors Love Emeralds',
          text: 'Emeralds are appreciating assets—top-quality stones from Zambia and Colombia have surged in value over the past decade. Their rarity, especially when untreated, makes them exceptionally desirable.'
        }
      ]
    },
    'ruby-guide': {
      title: 'Ruby',
      subtitle: 'The Flame of Passion & Power',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/qm5wph25_B13B369F-7304-410A-A966-07E267DEAF7E.png',
      content: [
        {
          heading: 'Origins We Offer',
          items: [
            '<strong>Burma (Myanmar)</strong> — The legendary &quot;pigeon blood&quot; rubies',
            '<strong>Mozambique</strong> — Modern high-quality rubies with rich red saturation',
            '<strong>Thailand</strong> — Deep, garnet-like reds, ideal for jewelry'
          ]
        },
        {
          heading: 'Why Rubies Are Exceptional',
          items: [
            'Long considered the most powerful gemstone.',
            'Symbolizes love, vitality, and protection.',
            'Burmese rubies are among the world&apos;s most expensive gems—top pieces outprice diamonds.'
          ]
        },
        {
          heading: 'Why Collectors Love Rubies',
          text: 'Rubies have a near-mythical status. A fine ruby is significantly rarer than a diamond, making it an extraordinary heirloom and investment.'
        }
      ]
    },
    'sapphire-guide': {
      title: 'Sapphire',
      subtitle: 'The Gem of Wisdom & Nobility',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/8627k2of_A3D9625E-612F-4417-A540-193F15E44D53.png',
      content: [
        {
          heading: 'Origins We Offer',
          items: [
            '<strong>Sri Lanka (Ceylon)</strong> — Bright, velvety blues; highly prized',
            '<strong>Kashmir</strong> — Extremely rare, cornflower blue, museum-grade',
            '<strong>Madagascar</strong> — Modern source with vivid saturation and clarity'
          ]
        },
        {
          heading: 'Why Sapphires Are Exceptional',
          items: [
            'Associated with wisdom, truth, and calm.',
            'Historically gifted to royalty and featured in crown jewels.',
            'Available in many colors—pink, yellow, teal, purple—each with beauty and personality.'
          ]
        },
        {
          heading: 'Why Collectors Love Sapphires',
          text: 'Ceylon and Kashmir sapphires continue to rise in value due to rarity and demand. Their durability and brilliance make them perfect for both investment and daily wear.'
        }
      ]
    },
    'panjshir-guide': {
      title: 'Panjshir Emerald',
      subtitle: 'Legendary Afghan Treasures',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/sn2sn0v1_IMG_3772.png',
      content: [
        {
          heading: 'The Mystique of Panjshir',
          text: 'Nestled within the rugged peaks of Afghanistan&apos;s Hindu Kush mountains lies the legendary Panjshir Valley—a source of emeralds so magnificent they have captivated connoisseurs for millennia. These extraordinary gemstones possess an ethereal luminosity and crystalline perfection that rivals the world&apos;s finest emeralds.'
        },
        {
          heading: 'Why Panjshir Emeralds Are Exceptional',
          items: [
            'Sublime verdant hues ranging from enchanting mint to deep forest green',
            'Extraordinary transparency and exceptional clarity—a hallmark of Panjshir stones',
            'Exquisite hexagonal crystal formations with mesmerizing internal gardens',
            'Coveted by ancient Persian royalty and Mughal emperors',
            'Each stone embodies centuries of geological artistry from one of Earth&apos;s most remote sources'
          ]
        },
        {
          heading: 'The Rarity & Allure',
          text: 'Panjshir emeralds represent one of gemology&apos;s most captivating secrets. Mining these treasures requires traversing treacherous mountain terrain, making each stone a testament to both nature&apos;s magnificence and human determination. Their scarcity, combined with their breathtaking beauty and storied provenance, positions Panjshir emeralds among the world&apos;s most desirable and collectible gemstones.'
        },
        {
          heading: 'Investment & Legacy',
          text: 'As one of the world&apos;s rarest emerald sources, Panjshir stones command extraordinary value among discerning collectors. Their unique geological characteristics, combined with limited availability, ensure these magnificent gems remain timeless heirlooms—appreciating treasures that transcend generations with their sublime elegance and historical significance.'
        }
      ]
    }
  };

  const post = posts[slug];

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl mb-4">Post Not Found</h1>
          <Button onClick={() => navigate('/blog')}>Back to Education</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Back Button */}
      <div className="py-6 px-4 border-b">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Education
          </Button>
        </div>
      </div>

      {/* Hero Image */}
      <section className="relative h-[60vh] md:h-[70vh]">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white px-4 max-w-4xl"
          >
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-3 md:mb-4">{post.title}</h1>
            <p className="text-xl sm:text-2xl md:text-3xl font-light">{post.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          {post.content.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="mb-10 md:mb-12"
            >
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl mb-4 md:mb-6">{section.heading}</h2>
              
              {section.items && (
                <ul className="space-y-3 md:space-y-4">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start">
                      <span className="w-2 h-2 bg-black rounded-full mr-3 md:mr-4 mt-2 flex-shrink-0"></span>
                      <span className="text-base md:text-lg text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: item }}></span>
                    </li>
                  ))}
                </ul>
              )}
              
              {section.text && (
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">{section.text}</p>
              )}
            </motion.div>
          ))}

          {/* CTA */}
          <div className="mt-12 md:mt-16 pt-12 md:pt-16 border-t text-center">
            <h3 className="font-serif text-2xl md:text-3xl mb-4 md:mb-6">Explore Our {post.title} Collection</h3>
            <Button
              onClick={() => navigate(`/shop?gem_type=${post.title.toLowerCase().replace('panjshir ', '')}`)}
              className="bg-black text-white hover:bg-gray-900"
              size="lg"
            >
              Browse {post.title}s
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
